import React, { useContext, useRef, useState } from "react";
import { Card, Typography, Button, Space, message } from "antd";
import {
  CheckCircleOutlined,
  UserAddOutlined,
  LoginOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { PlanarianTag } from "../../../Shared/Components/Display/PlanarianTag";
import { useNavigate } from "react-router-dom"; // Add this import for navigation
import { AcceptInvitationVm } from "../../User/Models/AcceptInvitationVm";
import { ApiErrorResponse } from "../../../Shared/Models/ApiErrorResponse";
import { UserService } from "../../User/UserService";
import { SwitchAccountComponent } from "./SwitchAccountComponent";
import { PlanarianButton } from "../../../Shared/Components/Buttons/PlanarianButtton";
import { DeleteButtonComponent } from "../../../Shared/Components/Buttons/DeleteButtonComponent";
import { AppContext } from "../../../Configuration/Context/AppContext";

const { Title, Text } = Typography;

interface InvitationComponentProps {
  invitation?: AcceptInvitationVm;
  invitationCode: string;
  isLoading: boolean;
}

const InvitationComponent = ({
  invitation,
  isLoading,
  invitationCode,
}: InvitationComponentProps) => {
  const [status, setStatus] = useState<"pending" | "accepted" | "declined">(
    "pending"
  );
  const { isAuthenticated, switchAccount, refreshPendingInvitations } =
    useContext(AppContext);

  const navigate = useNavigate();

  const [isDeclining, setIsDeclining] = useState<boolean>(false);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const invitationActionInFlightRef = useRef(false);
  const [showSwitchAccountModal, setShowSwitchAccountModal] = useState(false);

  const handleAccept = async () => {
    if (invitation === undefined || invitationActionInFlightRef.current) return;

    invitationActionInFlightRef.current = true;
    setIsAccepting(true);
    try {
      await UserService.AcceptInvitation(invitationCode);
      setStatus("accepted");
      message.success("You have accepted the invitation.");

      switchAccount(invitation.accountId, "/caves");
    } catch (error) {
      const err = error as ApiErrorResponse;
      message.error(err.message);
    } finally {
      invitationActionInFlightRef.current = false;
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (invitationActionInFlightRef.current) return;

    invitationActionInFlightRef.current = true;
    setIsDeclining(true);
    try {
      await UserService.DeclineInvitation(invitationCode);
      setStatus("declined");
      message.warning("You have declined the invitation.");
      void refreshPendingInvitations().catch(() => {});
    } catch (error) {
      const err = error as ApiErrorResponse;
      message.error(err.message);
    } finally {
      invitationActionInFlightRef.current = false;
      setIsDeclining(false);
    }
  };
  const handleCreateAccount = () => {
    navigate(`/register?invitationCode=${invitationCode}`);
  };

  const handleExistingAccount = () => {
    navigate(`/login?invitationCode=${invitationCode}`);
  };

  let invitationMessage = `You’ve been invited to access ${invitation?.accountName} data on
  Planarian.`;

  if (isAuthenticated) {
    invitationMessage += " Accept the invitation to continue!";
  } else {
    invitationMessage +=
      " Create an account or login to accept the invitation.";
  }

  if (!isLoading && !invitation) {
    return (
      <div style={styles.container}>
        <Card
          style={styles.card}
          styles={{ body: styles.cardBody }}
          bordered={false}
        >
          <Title level={3} style={{ marginBottom: 10 }}>
            Invitation Not Found
          </Title>
          <Text>
            We could not find an invitation with the provided code. Please check
            the code and try again.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {status === "pending" ? (
        <Card
          loading={isLoading}
          style={styles.card}
          styles={{ body: styles.cardBody }}
          bordered={false}
        >
          <Title level={3} style={{ marginBottom: 10 }}>
            {invitation?.accountName}
          </Title>
          <Text>{invitationMessage}</Text>

          <div style={styles.section}>
            <Text strong>Regions:</Text>
            <div style={styles.tags}>
              {invitation?.regions.map((region) => (
                <PlanarianTag
                  key={region}
                  color="blue"
                  icon={<EnvironmentOutlined />}
                  style={styles.tag}
                >
                  {region}
                </PlanarianTag>
              ))}
            </div>
          </div>

          {isAuthenticated ? (
            <Space style={{ marginTop: 20 }}>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleAccept}
                loading={isAccepting}
                disabled={isAccepting || isDeclining}
              >
                Accept
              </Button>
              <DeleteButtonComponent
                loading={isDeclining}
                disabled={isAccepting || isDeclining}
                title={`Are you sure you want to decline the invitation?`}
                onConfirm={() => {
                  handleDecline();
                }}
                okText="Yes"
                cancelText="No"
              >
                Decline
              </DeleteButtonComponent>
            </Space>
          ) : (
            <div style={styles.buttonStack}>
              <PlanarianButton
                type="primary"
                icon={<UserAddOutlined />}
                block
                onClick={handleCreateAccount}
                alwaysShowChildren
              >
                Create a New Account
              </PlanarianButton>
              <PlanarianButton
                type="default"
                icon={<LoginOutlined />}
                block
                onClick={handleExistingAccount}
                alwaysShowChildren
              >
                I Have an Account
              </PlanarianButton>
              <DeleteButtonComponent
                loading={isDeclining}
                disabled={isDeclining}
                title="Are you sure you want to decline the invitation?"
                onConfirm={() => {
                  void handleDecline();
                }}
                okText="Yes"
                cancelText="No"
                block
                alwaysShowChildren
              >
                Decline
              </DeleteButtonComponent>
            </div>
          )}
        </Card>
      ) : (
        <Card
          style={styles.card}
          styles={{ body: styles.cardBody }}
          bordered={false}
        >
          <Title level={3} style={{ marginBottom: 10 }}>
            {status === "accepted" ? "Access Confirmed" : "Invitation Declined"}
          </Title>
          <Text>
            {status === "accepted"
              ? "You now have access to the cave location data."
              : "If you change your mind, contact the survey organization."}
          </Text>
          {status === "accepted" && (
            <Button
              type="primary"
              onClick={() => setShowSwitchAccountModal(true)}
              style={{ marginTop: 20 }}
            >
              Switch Account
            </Button>
          )}
          <SwitchAccountComponent
            isVisible={showSwitchAccountModal}
            handleCancel={function (): void {
              setShowSwitchAccountModal(false);
            }}
          />
        </Card>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
    flex: 1,
    minHeight: "inherit",
    background: "var(--background-color)",
    color: "var(--text-color)",
    padding: "clamp(16px, 4vh, 32px)",
    width: "100%",
  },
  card: {
    boxSizing: "border-box",
    width: "min(420px, 100%)",
    textAlign: "center",
    boxShadow: "0px 6px 12px rgba(0,0,0,0.1)",
    borderRadius: 8,
    background: "var(--surface-color)",
    color: "var(--text-color)",
  },
  cardBody: {
    padding: "clamp(16px, 3vh, 24px)",
  },
  section: {
    marginTop: 16,
    textAlign: "left",
  },
  tags: {
    marginTop: 8,
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  tag: {
    padding: "4px 10px",
    fontSize: "14px",
  },
  buttonStack: {
    marginTop: 20,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
};

export { InvitationComponent };
